import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { AuthBrand } from '../components/AuthBrand';

/**
 * Link zum Zurücksetzen des Passworts anfordern. Die Antwort ist immer dieselbe, auch wenn es die Angaben nicht gibt, damit
 * niemand herausfinden kann, wer einen Zugang hat.
 */
export function ForgotPasswordPage() {
  const [tenant, setTenant] = useState(() => {
    try {
      return localStorage.getItem('kontor:tenant') ?? '';
    } catch {
      return '';
    }
  });
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const request = useMutation({
    mutationFn: () => api<{ data: { message: string } }>('POST', '/auth/password-reset', { tenant: tenant.replace(/\s+/g, '').toLowerCase(), email: email.trim() }),
    onError: (e) =>
      setError(
        e instanceof ApiError
          ? e.status === 429
            ? 'Zu viele Versuche. Bitte warten Sie einige Minuten.'
            : (Object.values(e.fields)[0] ?? e.message)
          : 'Die Anfrage hat nicht geklappt.',
      ),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    request.mutate();
  }

  return (
    <div className="login">
      <AuthBrand title={<>Passwort <span className="hl">vergessen?</span></>} text="Wir schicken Ihnen einen Link, über den Sie ein neues Passwort festlegen." />

      <main className="login__main">
        {request.isSuccess ? (
          <div className="card login__card">
            <h2>E-Mail unterwegs</h2>
            <p>{request.data.data.message}</p>
            <p className="muted small">Kommt nichts an, prüfen Sie den Spam-Ordner. Falls Ihr Unternehmen den Versand von E-Mails noch nicht eingerichtet hat, wenden Sie sich an Ihren Administrator.</p>
            <Link className="btn btn--primary btn--block" to="/login">Zur Anmeldung</Link>
          </div>
        ) : (
          <form className="card login__card" onSubmit={submit} noValidate>
            <h2>Link anfordern</h2>
            {error && <div className="alert alert--error" role="alert">{error}</div>}
            <label className="field">
              <span>Mandantennummer</span>
              <input className="input" value={tenant} onChange={(e) => setTenant(e.target.value)} autoComplete="organization" inputMode="numeric" placeholder="z. B. 4829173" />
            </label>
            <label className="field">
              <span>E-Mail</span>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus={!!tenant} />
            </label>
            <button className="btn btn--primary btn--block" type="submit" disabled={request.isPending || !tenant || !email}>
              {request.isPending ? 'Wird gesendet …' : 'Link anfordern'}
            </button>
            <p className="muted small"><Link to="/login">Zurück zur Anmeldung</Link></p>
          </form>
        )}
      </main>
    </div>
  );
}
